import { Component, computed, inject, input, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import * as fromSeriousSystem from '@serious-system';
import * as fromGenerated from '../../_generated';
import * as fromLayout from '../../layout';
import * as fromAssistantIcon from '../assistants/assistants.icon.helper';
import { ChatsHelper } from '../chats.helper';
import { TopBarMobileResponsive } from '../components/top-bar.mobile.responsive';
import * as fromShared from '../shared';
import { ChatActions, ChatModalActions } from '../store/chats.actions';
import { ChatsState } from '../store/chats.reducer';

@Component({
  selector: 'squadbox-conversation-top-bar-mobile',
  imports: [
    fromSeriousSystem.UseAssistantCustomIconDirective,
    fromSeriousSystem.UseAssistantIconDirective,
    TopBarMobileResponsive,
    fromSeriousSystem.DrawerComponent,
    fromSeriousSystem.OptionMenuComponent,
  ],
  providers: [ChatsHelper],
  template: `
    <squadbox-top-bar-mobile
      [hideMoreOptionsButton]="isNewConversation() && !selectedAssistant()"
      (chevronLeftButtonClicked)="handleClickChevronLeftButtonCTA()"
      (moreOptionsClicked)="isMoreOptionsOpened.set(true)"
    >
      @let assistant = selectedAssistant();
      @if (assistant && assistant.name) {
        <div
          class="
            flex flex-row items-center gap-4 [&>svg]:text-primary-500/50
            [&>img,&>svg]:size-6 [&>img,&>svg]:shrink-0
          "
        >
          @if (!isNewConversation()) {
            @switch (fromAssistantIcon.getLogoType(assistant.logo)) {
              @case ('assistant') {
                <svg
                  [sdUseAssistantIcon]="
                    fromAssistantIcon.getAssistantIcon(assistant.logo)
                  "
                ></svg>
              }
              @case ('custom') {
                <svg
                  [sdUseAssistantCustomIcon]="
                    fromAssistantIcon.getAssistantCustomIcon(assistant.logo)
                  "
                ></svg>
              }
              @default {
                <img [src]="assistant.logo" alt="Assistant Logo" />
              }
            }
            <div class="flex flex-col leading-6 overflow-hidden">
              <span class="typo-p2 font-semibold  truncate">
                {{ assistant.name }}
              </span>
              <span
                class="typo-p3 text-neutral-500 truncate"
                [hidden]="assistant && isNewConversation()"
              >
                {{ selectedChatTitle() }}
              </span>
            </div>
          }
        </div>
      } @else {
        <span
          class="typo-p2 font-semibold text-center w-full block truncate"
          [hidden]="isNewConversation()"
        >
          {{ selectedChatTitle() }}
        </span>
      }
    </squadbox-top-bar-mobile>

    <!-- More options -->
    @if (isMobile()) {
      <sd-drawer
        [isOpen]="isMoreOptionsOpened()"
        (closeClicked)="isMoreOptionsOpened.set(false)"
      >
        @for (option of drawerOptions(); track option.label) {
          <sd-option-menu
            [option]="option"
            (triggerAction)="onTriggerAction($event)"
          ></sd-option-menu>
        }
      </sd-drawer>
    }
  `,
  styles: [
    `
      @reference '../../../styles.css';

      :host {
        @apply block bg-shades-white;
      }
    `,
  ],
})
export class ConversationTopBarMobileResponsive {
  protected readonly fromAssistantIcon = fromAssistantIcon;
  private readonly chatsStore = inject<Store<ChatsState>>(Store);
  private readonly layoutStore = inject<Store<fromLayout.LayoutState>>(Store);
  private readonly chatsHelper = inject(ChatsHelper);
  private readonly mobileNavigation = inject(
    fromShared.MobileNavigationService,
  );

  public readonly selectedChatTitle = input.required<string>();
  public readonly selectedAssistant = input<Pick<
    fromGenerated.AssistantView,
    'name' | 'logo'
  > | null>();
  public readonly isNewConversation = input<boolean>(false);

  public readonly isMoreOptionsOpened = signal(false);
  public readonly isMobile = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectIsMobile,
  );
  public readonly drawerOptions = computed<fromSeriousSystem.OptionMenu[]>(() =>
    this.chatsHelper.getChatDrawerOptions({
      withAssistantProfile: !!this.selectedAssistant(),
      withRename: !this.isNewConversation(),
      withDelete: !this.isNewConversation(),
    }),
  );

  public async handleClickChevronLeftButtonCTA() {
    await this.mobileNavigation.toggle();
  }

  public onTriggerAction(action: string) {
    this.isMoreOptionsOpened.set(false);

    if (action === 'rename') {
      this.chatsStore.dispatch(ChatModalActions.openRenameChatModal());
    }
    if (action === 'delete') {
      this.chatsStore.dispatch(ChatModalActions.openDeleteChatModal());
    }
    if (action === 'assistant-profile') {
      this.chatsStore.dispatch(ChatActions.openAssistantProfile());
    }
  }
}
